import { ListCallStatistics } from '@/types/shared';
import { Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { SVGProps } from 'react';

type CallStatisticsProps = {
  callStatistics: ListCallStatistics | undefined;
  hideAnswerRate?: boolean;
  hideMIs?: boolean;
};

function CircularProgressWithLabel({
  value,
  primaryColor,
  secondaryColor,
}: {
  value: number;
  primaryColor: string;
  secondaryColor: string;
}) {
  const theme = useTheme();
  const isValueUndefined = Number.isNaN(value);

  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = (1 / 4) * circumference;
  const strokeDasharray = (value / 100) * circumference;

  const BaseCircle = (props: SVGProps<SVGCircleElement>) => (
    <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="10" {...props} />
  );

  return (
    <Box
      sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box sx={{
        display: "flex"
      }}>
        <svg viewBox="0 0 100 100" height={'4.75rem'}>
          {/* eslint-disable-next-line react-hooks/static-components -- inner SVG circle helper; extract later */}
          <BaseCircle
            stroke={isValueUndefined ? theme.palette.grey[900] : secondaryColor}
            opacity={'10%'}
          />
          {value > 0 && (
            // eslint-disable-next-line react-hooks/static-components -- inner SVG circle helper; extract later
            <BaseCircle
              strokeLinecap="round"
              stroke={primaryColor}
              strokeDasharray={`${strokeDasharray} ${circumference - strokeDasharray}`}
              strokeDashoffset={strokeOffset}
            />
          )}
        </svg>
      </Box>
      <Box sx={{
        position: "absolute"
      }}>
        <Typography variant="subtitle2">
          {isValueUndefined ? 'N/A' : `${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const ProgressIndicator = ({
  title,
  titleTooltip,
  denominator,
  numerator,
}: {
  title: string;
  titleTooltip: string;
  denominator: number;
  numerator: number;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}>
      <Tooltip title={titleTooltip} arrow placement="top">
        <Typography variant="body2">{title}</Typography>
      </Tooltip>
      <Tooltip title={`${numerator}/${denominator}`} arrow>
        <span>
          <CircularProgressWithLabel
            primaryColor={theme.palette.primary.main}
            secondaryColor={theme.palette.primary.light}
            value={(numerator / denominator) * 100}
          />
        </span>
      </Tooltip>
    </Box>
  );
};

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: 'space-between',
        gap: '2rem'
      }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function skeletonDimensions({ hideAnswerRate, hideMIs }: Partial<CallStatisticsProps>) {
  let width = 24;
  if (hideAnswerRate) width -= 5;
  if (hideMIs) width -= 5;
  return {
    width: `${width}rem`,
    height: '7.5rem',
  };
}

export default function CallStatistics({
  callStatistics,
  hideAnswerRate,
  hideMIs,
}: CallStatisticsProps) {
  if (!callStatistics) {
    return <Skeleton variant="rectangular" {...skeletonDimensions({ hideAnswerRate, hideMIs })} />;
  }

  const answered = callStatistics.Answered + callStatistics['Meaningful interaction']; // can be 0
  const called = answered + callStatistics['No answer']; // can be 0

  const completed = called + callStatistics.Skipped;

  const listTotal = completed + callStatistics.Uncalled;

  return (
    <Box
      sx={{
        alignSelf: "start",
        display: "flex",
        gap: "2rem"
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: 'center',
          gap: '0.25rem'
        }}>
        <Typography variant="subtitle2">Call statistics</Typography>
        <Box
          sx={{
            display: "flex",
            gap: '0.75rem'
          }}>
          <ProgressIndicator
            titleTooltip="Skipped & called / Total list"
            title="Completed"
            numerator={completed}
            denominator={listTotal}
          />
          {!hideAnswerRate && (
            <ProgressIndicator
              titleTooltip="Answers / Total attempts"
              title="Answer rate"
              numerator={answered}
              denominator={called}
            />
          )}
          {!hideMIs && (
            <ProgressIndicator
              titleTooltip="Meaningful interactions / Total answers"
              title="MI rate"
              numerator={callStatistics['Meaningful interaction']}
              denominator={answered}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem"
        }}>
        <StatRow label="Uncalled" value={callStatistics.Uncalled.toString()} />
        <StatRow label="Called" value={called.toString()} />
        <StatRow label="Skipped" value={callStatistics.Skipped.toString()} />
        <StatRow label="MIs" value={callStatistics['Meaningful interaction'].toString()} />
        <StatRow label="List total" value={listTotal.toString()} />
      </Box>
    </Box>
  );
}
